import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import PatternHintsBanner from '@/components/PatternHintsBanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search, User, Check, X, ChevronRight, ArrowRight, Shield,
  FileText, FlaskConical, Calendar, ClipboardList, BookOpen,
  TestTube, FolderOpen, AlertTriangle, Eye, ExternalLink,
  CheckCircle2, Circle, Loader2, ShieldCheck, RefreshCw
} from 'lucide-react';

const STEP_META = {
  account: { label: 'Account', icon: Shield, color: '#7BA3C8' },
  intake: { label: 'Intake', icon: FileText, color: '#C89B7B' },
  documents: { label: 'Documents', icon: FolderOpen, color: '#9B7BC8' },
  decode: { label: 'DECODE', icon: FlaskConical, color: '#E07B7B' },
  phase: { label: 'Phase', icon: Calendar, color: '#4E7A6B' },
  protocol: { label: 'Protocol', icon: ClipboardList, color: '#C8A87B' },
  labs: { label: 'Labs', icon: TestTube, color: '#7BADC8' },
  knowledge: { label: 'Knowledge', icon: BookOpen, color: '#C87BAD' },
};

const STATUS_COLORS = {
  completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  not_started: { bg: 'bg-white/[0.03]', text: 'text-adm-tertiary', border: 'border-white/[0.06]' },
};

/* ── Subscriber row in tracker ──────────────── */
function OnboardingRow({ sub, onViewDetail }) {
  const pct = sub.completion_pct || 0;
  const barColor = pct === 100 ? '#4E7A6B' : pct >= 50 ? '#C89B7B' : '#E07B7B';
  const stepKeys = ['account', 'intake', 'documents', 'decode', 'phase', 'protocol', 'labs', 'knowledge'];

  return (
    <div className="flex items-center gap-4 p-3.5 rounded-lg bg-adm-bg/30 hover:bg-adm-bg/50 transition-colors" data-testid={`onboarding-row-${sub.id}`}>
      <div className="w-8 h-8 rounded-lg bg-adm-accent/10 flex items-center justify-center flex-shrink-0">
        <User className="w-4 h-4 text-adm-accent" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-adm-primary truncate">{sub.name || 'Unnamed'}</span>
          <span className="text-[10px] text-adm-tertiary truncate">{sub.email}</span>
        </div>
        {/* Step indicators */}
        <div className="flex items-center gap-1">
          {stepKeys.map(key => {
            const done = sub.step_statuses?.[key];
            const meta = STEP_META[key];
            return (
              <div key={key} className="flex items-center gap-0.5" title={meta.label}>
                <div className={`w-5 h-1.5 rounded-full ${done ? 'opacity-100' : 'opacity-20'}`} style={{ backgroundColor: meta.color }} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Completion % */}
        <div className="text-right">
          <span className="text-xs font-mono" style={{ color: barColor }}>{pct}%</span>
          <div className="w-16 h-1 bg-white/5 rounded-full mt-0.5 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
          </div>
        </div>
        {/* Intake badge */}
        {sub.intake_status && sub.intake_status !== 'not_started' && (
          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
            sub.intake_status === 'verified' ? 'border-emerald-500/30 text-emerald-400' :
            sub.intake_status === 'submitted' ? 'border-blue-500/30 text-blue-400' :
            'border-white/10 text-adm-tertiary'
          }`}>
            {sub.intake_status}
          </Badge>
        )}
        <Button variant="ghost" size="sm" onClick={() => onViewDetail(sub.id)}
          className="text-adm-tertiary hover:text-adm-primary h-7 px-2 text-xs" data-testid={`view-detail-${sub.id}`}>
          <Eye className="w-3 h-3 mr-1" /> Detail
        </Button>
      </div>
    </div>
  );
}

/* ── Detail panel ──────────────────────────── */
function OnboardingDetail({ subscriberId, onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);

  useEffect(() => {
    if (!subscriberId) return;
    setLoading(true);
    axios.get(`${API}/onboarding/${subscriberId}/status`).then(res => {
      setData(res.data);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [subscriberId]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res = await axios.get(`${API}/onboarding/${subscriberId}/validate`);
      setValidation(res.data);
    } catch { toast.error('Validation failed'); }
    finally { setValidating(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-adm-accent animate-spin" />
    </div>
  );

  if (!data) return null;

  const stepKeys = ['account', 'intake', 'documents', 'decode', 'phase', 'protocol', 'labs', 'knowledge'];
  const STEP_LINKS = {
    account: '/admin/users',
    intake: '/admin/biological-context',
    documents: '/admin/documents',
    decode: `/admin/decode-wizard?subscriber=${subscriberId}`,
    phase: `/admin/protocol-wizard?subscriber=${subscriberId}`,
    protocol: `/admin/executable-protocol?subscriber=${subscriberId}`,
    labs: `/admin/lab-intelligence?subscriber=${subscriberId}`,
    knowledge: '/admin/content-publishing',
  };

  return (
    <div className="space-y-4" data-testid="onboarding-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-adm-primary text-sm font-medium font-['Work_Sans']">{data.subscriber_name}</h3>
          <p className="text-adm-tertiary text-[10px]">{data.completed_steps}/{data.total_steps} steps complete</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleValidate} disabled={validating}
            className="border-white/10 text-adm-secondary hover:text-adm-primary text-xs h-7" data-testid="validate-account-btn">
            {validating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
            Validate Account
          </Button>
          <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${
            data.is_go_live_ready ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/8' : 'border-amber-500/30 text-amber-400 bg-amber-500/8'
          }`} data-testid="go-live-badge">
            {data.is_go_live_ready ? 'Go-Live Ready' : 'Not Ready'}
          </Badge>
        </div>
      </div>

      {/* Completion bar */}
      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${data.completion_pct}%`, backgroundColor: data.completion_pct === 100 ? '#4E7A6B' : '#C89B7B' }} />
      </div>

      {/* Pattern hints (shared deterministic banner) */}
      <PatternHintsBanner subscriberId={subscriberId} viewerRole={user?.role} />

      {/* Validation results */}
      {validation && (
        <Card className="bg-adm-bg/50 border-white/[0.06] p-3" data-testid="validation-results">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-adm-primary">Account Validation</span>
            <Badge variant="outline" className={`text-[10px] ${validation.is_valid ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
              {validation.passed}/{validation.total} passed
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(validation.checks).map(([key, passed]) => (
              <div key={key} className="flex items-center gap-1.5">
                {passed ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3 text-red-400" />}
                <span className={`text-[10px] ${passed ? 'text-adm-secondary' : 'text-red-400'}`}>
                  {key.replace(/^has_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              </div>
            ))}
          </div>
          {validation.missing.length > 0 && (
            <p className="text-[10px] text-red-400/70 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Missing: {validation.missing.join(', ')}
            </p>
          )}
        </Card>
      )}

      {/* Steps grid */}
      <div className="space-y-1.5">
        {stepKeys.map(key => {
          const step = data.steps[key] || {};
          const meta = STEP_META[key];
          const Icon = meta.icon;
          const sc = STATUS_COLORS[step.status] || STATUS_COLORS.not_started;
          const link = STEP_LINKS[key];
          return (
            <div key={key} className={`flex items-center gap-3 p-3 rounded-lg border ${sc.border} ${sc.bg} transition-colors`}
              data-testid={`step-${key}`}>
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}15` }}>
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-adm-primary">{meta.label}</span>
                {key === 'intake' && step.sections_done !== undefined && (
                  <span className="text-[10px] text-adm-tertiary ml-2">{step.sections_done}/{step.sections_total} sections</span>
                )}
                {key === 'documents' && step.count !== undefined && (
                  <span className="text-[10px] text-adm-tertiary ml-2">{step.count} files</span>
                )}
                {key === 'protocol' && step.item_count !== undefined && (
                  <span className="text-[10px] text-adm-tertiary ml-2">{step.item_count} items</span>
                )}
                {key === 'labs' && step.report_count !== undefined && (
                  <span className="text-[10px] text-adm-tertiary ml-2">{step.report_count} reports</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {step.status === 'completed' ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : step.status === 'in_progress' ? (
                  <Circle className="w-4 h-4 text-amber-400" />
                ) : (
                  <Circle className="w-4 h-4 text-adm-tertiary/30" />
                )}
                <a href={link} className="text-adm-tertiary hover:text-adm-primary transition-colors" data-testid={`goto-${key}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Go-Live rule */}
      {!data.is_go_live_ready && (
        <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-200/80 leading-relaxed">
              Subscriber cannot be marked Active/Live until: Intake exists, Protocol exists, and minimum 1 document uploaded.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main CareOps + Onboarding Page ────────── */
export default function CareOps() {
  const [tab, setTab] = useState('onboarding');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCompletion, setFilterCompletion] = useState('all');
  const [detailSub, setDetailSub] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/onboarding/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      toast.error('Failed to load onboarding dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const subs = (dashboard?.subscribers || []).filter(s => {
    if (search) {
      const q = search.toLowerCase();
      if (!s.name?.toLowerCase().includes(q) && !s.email?.toLowerCase().includes(q)) return false;
    }
    if (filterCompletion === 'complete' && s.completion_pct < 100) return false;
    if (filterCompletion === 'incomplete' && s.completion_pct >= 100) return false;
    if (filterCompletion === 'not_started' && s.completion_pct > 0) return false;
    return true;
  });

  // Stats
  const total = dashboard?.subscribers?.length || 0;
  const complete = (dashboard?.subscribers || []).filter(s => s.completion_pct === 100).length;
  const inProgress = (dashboard?.subscribers || []).filter(s => s.completion_pct > 0 && s.completion_pct < 100).length;
  const notStarted = (dashboard?.subscribers || []).filter(s => s.completion_pct === 0).length;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto" data-testid="care-ops-page">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-adm-primary font-['Work_Sans'] tracking-tight" data-testid="care-ops-title">
            Care Operations
          </h1>
          <p className="text-adm-tertiary text-xs mt-1">Subscriber onboarding, intake tracking, and document management</p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-adm-bg/50 border border-white/[0.06] p-0.5">
            <TabsTrigger value="onboarding" className="text-xs data-[state=active]:bg-adm-panel data-[state=active]:text-adm-primary" data-testid="tab-onboarding">
              Onboarding Tracker
            </TabsTrigger>
            <TabsTrigger value="intake" className="text-xs data-[state=active]:bg-adm-panel data-[state=active]:text-adm-primary" data-testid="tab-intake">
              Intake Dashboard
            </TabsTrigger>
          </TabsList>

          {/* TAB: Onboarding Tracker */}
          <TabsContent value="onboarding" className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-3" data-testid="onboarding-stats">
              <Card className="bg-adm-panel border-white/[0.06] p-3">
                <span className="text-adm-tertiary text-[10px] uppercase tracking-wider">Total</span>
                <span className="text-xl font-mono text-adm-primary block mt-0.5">{total}</span>
              </Card>
              <Card className="bg-emerald-500/5 border-emerald-500/15 p-3">
                <span className="text-emerald-400/60 text-[10px] uppercase tracking-wider">Complete</span>
                <span className="text-xl font-mono text-emerald-400 block mt-0.5">{complete}</span>
              </Card>
              <Card className="bg-amber-500/5 border-amber-500/15 p-3">
                <span className="text-amber-400/60 text-[10px] uppercase tracking-wider">In Progress</span>
                <span className="text-xl font-mono text-amber-400 block mt-0.5">{inProgress}</span>
              </Card>
              <Card className="bg-red-500/5 border-red-500/15 p-3">
                <span className="text-red-400/60 text-[10px] uppercase tracking-wider">Not Started</span>
                <span className="text-xl font-mono text-red-400 block mt-0.5">{notStarted}</span>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-adm-tertiary" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subscriber..."
                  className="pl-8 bg-adm-panel border-white/[0.06] text-adm-primary text-xs h-8" data-testid="onboarding-search" />
              </div>
              <Select value={filterCompletion} onValueChange={setFilterCompletion}>
                <SelectTrigger className="w-36 bg-adm-panel border-white/[0.06] text-adm-secondary text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-adm-panel border-white/10">
                  <SelectItem value="all" className="text-adm-primary text-xs">All Status</SelectItem>
                  <SelectItem value="complete" className="text-adm-primary text-xs">Complete</SelectItem>
                  <SelectItem value="incomplete" className="text-adm-primary text-xs">In Progress</SelectItem>
                  <SelectItem value="not_started" className="text-adm-primary text-xs">Not Started</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchDashboard} className="border-white/10 text-adm-secondary h-8 text-xs">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>

            {/* Subscriber list */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-adm-accent animate-spin" />
              </div>
            ) : subs.length === 0 ? (
              <Card className="bg-adm-panel border-white/[0.06] p-10 text-center">
                <User className="w-8 h-8 text-adm-tertiary mx-auto mb-2" strokeWidth={1} />
                <p className="text-adm-secondary text-sm">No subscribers found</p>
              </Card>
            ) : (
              <div className="space-y-1.5" data-testid="onboarding-list">
                {subs.map(s => (
                  <OnboardingRow key={s.id} sub={s} onViewDetail={setDetailSub} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB: Intake Dashboard */}
          <TabsContent value="intake" className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-adm-tertiary" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
                  className="pl-8 bg-adm-panel border-white/[0.06] text-adm-primary text-xs h-8" data-testid="intake-search" />
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-adm-accent animate-spin" /></div>
            ) : (
              <div className="bg-adm-surface/[0.03] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="intake-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-4 py-2.5 text-[10px] font-medium text-adm-tertiary uppercase tracking-wider">Subscriber</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-medium text-adm-tertiary uppercase tracking-wider">Intake Status</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-medium text-adm-tertiary uppercase tracking-wider">Completion</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-medium text-adm-tertiary uppercase tracking-wider">Last Updated</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-medium text-adm-tertiary uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map(s => (
                      <tr key={s.id} className="border-b border-white/[0.04] hover:bg-white/[0.01]" data-testid={`intake-row-${s.id}`}>
                        <td className="px-4 py-2.5">
                          <span className="text-xs text-adm-primary">{s.name}</span>
                          <span className="text-[10px] text-adm-tertiary ml-2">{s.email}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${
                            s.intake_status === 'verified' ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/8' :
                            s.intake_status === 'submitted' ? 'border-blue-500/30 text-blue-400 bg-blue-500/8' :
                            s.intake_status === 'draft' ? 'border-amber-500/30 text-amber-400 bg-amber-500/8' :
                            'border-white/10 text-adm-tertiary'
                          }`} data-testid={`intake-status-${s.id}`}>
                            {s.intake_status || 'Not Started'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{
                                width: `${s.intake_completion || 0}%`,
                                backgroundColor: s.intake_completion === 100 ? '#4E7A6B' : '#C89B7B'
                              }} />
                            </div>
                            <span className="text-[10px] font-mono text-adm-secondary">{s.intake_completion || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="text-[10px] text-adm-tertiary">
                            {s.intake_updated ? new Date(s.intake_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <a href="/admin/biological-context" className="text-adm-accent hover:text-adm-accent/80 text-[10px] inline-flex items-center gap-1">
                            Open <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        </td>
                      </tr>
                    ))}
                    {subs.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-10 text-adm-tertiary text-xs">No subscribers found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Detail side panel */}
        {detailSub && (
          <Dialog open={!!detailSub} onOpenChange={() => setDetailSub(null)}>
            <DialogContent className="bg-adm-surface border-white/[0.06] max-w-md max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
              <DialogHeader>
                <DialogTitle className="text-adm-primary text-sm font-['Work_Sans']">Onboarding Progress</DialogTitle>
              </DialogHeader>
              <OnboardingDetail subscriberId={detailSub} onClose={() => setDetailSub(null)} />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AdminLayout>
  );
}
